import { Order, OrderDeliveryType, Route, User } from "@web-app/types";
import { pluralize } from "@web-app/utils/helpers";

type SortParameterType =
  | "createdAt"
  | "eta"
  | "priority"
  | "scheduledEndDateTime"
  | "scheduledStartDateTime"
  | "updatedAt";

export type SortOptionType = {
  disabled: boolean;
  label: string;
  value: SortParameterType;
};

export const sortOptions: SortOptionType[] = [
  {
    disabled: false,
    label: "Pickup Time",
    value: "scheduledStartDateTime",
  },
  {
    disabled: false,
    label: "Drop-off Time",
    value: "scheduledEndDateTime",
  },
  {
    disabled: false,
    label: "Creation Time",
    value: "createdAt",
  },
  {
    disabled: false,
    label: "Last Modified",
    value: "updatedAt",
  },
  {
    disabled: false,
    label: "ETA",
    value: "eta",
  },
  {
    disabled: false,
    label: "Priority",
    value: "priority",
  },
];

type GroupOptions = {
  disabled: boolean;
  label: string;
  value:
    "connections-drivers" | "connections" | "locations" | "none" | "states";
};

export const groupOptions: GroupOptions[] = [
  {
    disabled: false,
    label: "Default",
    value: "none",
  },
  {
    disabled: false,
    label: "Drivers & Connections",
    value: "connections-drivers",
  },
  {
    disabled: false,
    label: "Connections",
    value: "connections",
  },
  {
    disabled: false,
    label: "Markets",
    value: "states",
  },
  {
    disabled: false,
    label: "Location",
    value: "locations",
  },
];

export const escapeRegExp = (text: string) =>
  text.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

export const generateSearchRegex = (searchTerm: string) => {
  return new RegExp(escapeRegExp(searchTerm), "i");
};

export const capitalizeWords = (sentence: string) => {
  return sentence
    .split(" ")
    .map((word: string) => (word ? word[0].toUpperCase() + word.slice(1) : ""))
    .join(" ");
};

const valueContainsSearchText = (searchText: string, value: string) => {
  const searchTextRegex = generateSearchRegex(searchText);

  if (value) {
    return (
      searchTextRegex.test(value) || searchTextRegex.test(value.toLowerCase())
    );
  }

  return false;
};

export const getSearchFieldsExceptOrderCard = (
  order: Order,
): Record<string, string> => {
  // fields not visible in the main card that should still match and show extra results
  return {
    dropoff_business_name: order.dropoff?.businessName?.trim() || "",
    pickup_business_name: order.pickup?.businessName?.trim() || "",
  };
};

export const filterOrdersBySearchTerm = (
  orders: Order[],
  searchTerm: string,
  assigneesMap?: Map<string, User>,
) => {
  if (!searchTerm) {
    return orders;
  }

  return orders.filter((order) => {
    const searchFieldsExceptOrderCard = getSearchFieldsExceptOrderCard(order);

    const pickupAddress = order?.pickup?.address;
    const dropoffAddress = order?.dropoff?.address;
    const dropoffBusinessName = order?.dropoff?.businessName;
    const pickupBusinessName = order?.pickup?.businessName;
    const orderDisplayId = order?.displayId;
    const recipientName = order?.dropoff?.name;
    const externalId = order?.externalId ?? "";
    const routeExternalId = order?.route?.externalId ?? "";
    const assignee = order?.assigneeId
      ? assigneesMap?.get(order.assigneeId)
      : null;
    const assigneeName = assignee?.name ?? "";

    const fieldsArray = [
      pickupAddress,
      dropoffAddress,
      pickupBusinessName,
      dropoffBusinessName,
      orderDisplayId,
      recipientName,
      externalId,
      routeExternalId,
      assigneeName,
      ...Object.values(searchFieldsExceptOrderCard),
    ];

    return fieldsArray.some((field) =>
      valueContainsSearchText(searchTerm, field),
    );

    // return (
    //   valueContainsSearchText(searchTerm, pickupAddress) ||
    //   valueContainsSearchText(searchTerm, dropoffAddress) ||
    //   valueContainsSearchText(searchTerm, pickupBusinessName) ||
    //   valueContainsSearchText(searchTerm, dropoffBusinessName) ||
    //   valueContainsSearchText(searchTerm, orderDisplayId) ||
    //   valueContainsSearchText(searchTerm, recipientName) ||
    //   valueContainsSearchText(searchTerm, externalId) ||
    //   valueContainsSearchText(searchTerm, routeExternalId) ||
    //   valueContainsSearchText(searchTerm, assigneeName)
    // );
  });
};

const getTimeInMS = (date: Date) => {
  return new Date(date).getTime();
};

const getEtaDateForPickupOrder = (order: Order) => {
  const etaDateTime =
    order.pickupEta?.dateTime ??
    order.estimatedEtaDateTime ??
    order.scheduledEndDateTime;

  return getTimeInMS(etaDateTime);
};

const getEtaDateForDropoffOrder = (order: Order) => {
  const etaDateTime =
    order.eta?.dateTime ??
    order.estimatedEtaDateTime ??
    order.scheduledEndDateTime;

  return getTimeInMS(etaDateTime);
};

export const sortOrders = (orders: Order[], sortOption: SortOptionType) => {
  if (sortOption.value === "scheduledStartDateTime") {
    return orders.sort((a, b) => {
      const aTime = new Date(a.scheduledStartDateTime).getTime();
      const bTime = new Date(b.scheduledStartDateTime).getTime();
      return aTime - bTime;
    });
  }

  if (sortOption.value === "scheduledEndDateTime") {
    return orders.sort((a, b) => {
      const aTime = new Date(a.scheduledEndDateTime).getTime();
      const bTime = new Date(b.scheduledEndDateTime).getTime();
      return aTime - bTime;
    });
  }

  if (sortOption.value === "createdAt") {
    return orders.sort((a, b) => {
      const aTime = new Date(a.createdAt).getTime();
      const bTime = new Date(b.createdAt).getTime();
      return aTime - bTime;
    });
  }

  if (sortOption.value === "updatedAt") {
    return orders.sort((a, b) => {
      const aTime = new Date(a.updatedAt).getTime();
      const bTime = new Date(b.updatedAt).getTime();
      return aTime - bTime;
    });
  }

  if (sortOption.value === "eta") {
    const p2pOrders = orders.filter(
      (order) => order.type === OrderDeliveryType.INDIVIDUAL_PICKUP_AND_DROPOFF,
    );
    const nonP2POrders = orders.filter(
      (order) => order.type !== OrderDeliveryType.INDIVIDUAL_PICKUP_AND_DROPOFF,
    );

    const sortedP2POrders = p2pOrders.sort((a, b) => {
      const aEta = getEtaDateForDropoffOrder(a);
      const bEta = getEtaDateForDropoffOrder(b);

      return aEta - bEta;
    });

    const sortedNonP2POrders = nonP2POrders.sort((a, b) => {
      const aEta =
        a.type === OrderDeliveryType.ROUTE_OPT_PICKUP
          ? getEtaDateForPickupOrder(a)
          : getEtaDateForDropoffOrder(a);
      const bEta =
        b.type === OrderDeliveryType.ROUTE_OPT_PICKUP
          ? getEtaDateForPickupOrder(b)
          : getEtaDateForDropoffOrder(b);

      return aEta - bEta;
    });

    // Keep the non-P2P orders or unrouted orders at the bottom of the list
    return [...sortedP2POrders, ...sortedNonP2POrders];
  }

  if (sortOption.value === "priority") {
    orders.sort((a, b) => {
      const aTime = new Date(a.scheduledStartDateTime).getTime();
      const bTime = new Date(b.scheduledStartDateTime).getTime();
      return aTime - bTime;
    });

    return orders.sort((a, b) => {
      const priorityOrder = { HIGH: 1, LOW: 2 };
      return (
        (priorityOrder[a.priority] || 3) - (priorityOrder[b.priority] || 3)
      );
    });
  }

  return orders;
};

export const sortRoutes = (
  routes: Route[],
  sortOption: SortOptionType,
  orders: Map<string, Order>,
) => {
  if (
    sortOption.value === "scheduledStartDateTime" ||
    sortOption.value === "priority" // as we donot hoave any priotiry option for route. we will sort it by scheduled start date time
  ) {
    return routes.sort((a, b) => {
      const aTime = new Date(a.scheduledDateTime).getTime();
      const bTime = new Date(b.scheduledDateTime).getTime();
      return aTime - bTime;
    });
  }

  if (sortOption.value === "scheduledEndDateTime") {
    return routes.sort((a, b) => {
      const aTime = new Date(a.scheduledDateTime).getTime();
      const bTime = new Date(b.scheduledDateTime).getTime();
      return aTime - bTime;
    });
  }

  if (sortOption.value === "createdAt") {
    return routes.sort((a, b) => {
      const aTime = new Date(a.createdAt).getTime();
      const bTime = new Date(b.createdAt).getTime();
      return aTime - bTime;
    });
  }

  if (sortOption.value === "updatedAt") {
    return routes.sort((a, b) => {
      const aTime = new Date(a.updatedAt).getTime();
      const bTime = new Date(b.updatedAt).getTime();
      return aTime - bTime;
    });
  }

  if (sortOption.value === "eta") {
    return routes.sort((a, b) => {
      const aOrders = a.ordersOrder;
      const bOrders = b.ordersOrder;

      const aLastOrder = orders.get(aOrders[aOrders.length - 1].id);
      const bLastOrder = orders.get(bOrders[bOrders.length - 1].id);

      if (!aLastOrder || !bLastOrder) {
        return 0;
      }

      const aEta =
        aLastOrder.type === OrderDeliveryType.ROUTE_OPT_PICKUP
          ? getEtaDateForPickupOrder(aLastOrder)
          : getEtaDateForDropoffOrder(aLastOrder);
      const bEta =
        bLastOrder.type === OrderDeliveryType.ROUTE_OPT_PICKUP
          ? getEtaDateForPickupOrder(bLastOrder)
          : getEtaDateForDropoffOrder(bLastOrder);

      return aEta - bEta;
    });
  }

  return routes;
};

export const statusOptions = [
  {
    disabled: false,
    label: "Unassigned",
    value: "TODO",
  },
  {
    disabled: true,
    label: "Assigned",
    value: "ASSIGNED",
  },
  {
    disabled: false,
    label: "In Transit",
    value: "INPROGRESS",
  },
  {
    disabled: false,
    label: "Completed",
    value: "COMPLETE",
  },
  {
    disabled: false,
    label: "Cancelled",
    value: "CANCELLED",
  },
  {
    disabled: false,
    label: "Failed",
    value: "FAILED",
  },
];

export const statusOptionsForIndividualOrder = [
  {
    disabled: false,
    label: "Unassigned",
    value: "TODO",
  },
  {
    disabled: true,
    label: "Assigned",
    value: "ASSIGNED",
  },
  {
    disabled: false,
    label: "In Transit to Pickup",
    value: "INPROGRESS_PICKUP",
  },
  {
    disabled: false,
    label: "In Transit to Dropoff",
    value: "INPROGRESS_DROPOFF",
  },
  {
    disabled: false,
    label: "Completed",
    value: "COMPLETE",
  },
  {
    disabled: false,
    label: "Cancelled",
    value: "CANCELLED",
  },
  {
    disabled: false,
    label: "Failed",
    value: "FAILED",
  },
];

export const getContentCountTexts = (routes: Route[], orders: Order[]) => {
  const texts = [];

  const totalIncompleteRoutes = routes.filter(
    (route) => route.status !== "COMPLETE",
  ).length;

  const totalCompleteRoutes = routes.filter(
    (route) => route.status === "COMPLETE",
  ).length;

  const totalIncompleteOrders = orders.filter(
    (order) => order.status !== "COMPLETE" && order.status !== "CANCELLED",
  ).length;

  const totalCompleteOrders = orders.filter(
    (order) => order.status === "COMPLETE",
  ).length;

  const totalCancelledOrders = orders.filter(
    (order) => order.status === "CANCELLED",
  ).length;

  if (totalCancelledOrders) {
    texts.push(
      `${totalCancelledOrders} ${pluralize("Cancelled Order", totalCancelledOrders)}`,
    );
  }

  if (totalIncompleteOrders) {
    texts.push(
      `${totalIncompleteOrders} ${pluralize("Pending Order", totalIncompleteOrders)}`,
    );
  }

  if (totalCompleteOrders) {
    texts.push(
      `${totalCompleteOrders} ${pluralize("Completed Order", totalCompleteOrders)}`,
    );
  }

  if (totalIncompleteRoutes) {
    texts.push(
      `${totalIncompleteRoutes} ${pluralize("Pending Route", totalIncompleteRoutes)}`,
    );
  }

  if (totalCompleteRoutes) {
    texts.push(
      `${totalCompleteRoutes} ${pluralize("Completed Route", totalCompleteRoutes)}`,
    );
  }

  return texts.join(" • ");
};
